import React, { useState, useRef } from 'react';
import { supabase } from '@/lib/supabase';

interface PhotoUploadProps {
  equipmentId: string;
  existingPhotos: string[];
  onPhotosUpdate: (photos: string[]) => void;
  maxPhotos?: number;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  equipmentId,
  existingPhotos,
  onPhotosUpdate,
  maxPhotos = 5
}) => {
  const [uploading, setUploading] = useState(false);
  const [photos, setPhotos] = useState<string[]>(existingPhotos);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (photos.length + files.length > maxPhotos) {
      alert(`Maximum ${maxPhotos} photos allowed`);
      return;
    }

    setUploading(true);

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          throw new Error('Only image files are allowed');
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error('File size must be less than 5MB');
        }

        // Create unique filename
        const fileExt = file.name.split('.').pop();
        const fileName = `${equipmentId}_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `equipment-photos/${fileName}`;

        // Upload file to Supabase Storage
        const { data, error } = await supabase!.storage
          .from('warranty-photos')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) throw error;

        // Get public URL
        const { data: { publicUrl } } = supabase!.storage
          .from('warranty-photos')
          .getPublicUrl(filePath);

        return publicUrl;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      const updatedPhotos = [...photos, ...uploadedUrls];

      // Update equipment photos in database
      const { error: updateError } = await supabase!
        .from('equipment')
        .update({ photos: updatedPhotos })
        .eq('id', equipmentId);

      if (updateError) throw updateError;

      setPhotos(updatedPhotos);
      onPhotosUpdate(updatedPhotos);

    } catch (error) {
      console.error('Photo upload error:', error);
      alert(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removePhoto = async (photoUrl: string) => {
    try {
      // Extract file path from URL
      const urlParts = photoUrl.split('/');
      const filePath = `equipment-photos/${urlParts[urlParts.length - 1]}`;

      // Delete from storage
      const { error: deleteError } = await supabase!.storage
        .from('warranty-photos')
        .remove([filePath]);

      if (deleteError) {
        console.warn('Failed to delete from storage:', deleteError);
      }

      // Update photos array
      const updatedPhotos = photos.filter(url => url !== photoUrl);

      // Update equipment in database
      const { error: updateError } = await supabase!
        .from('equipment')
        .update({ photos: updatedPhotos })
        .eq('id', equipmentId);

      if (updateError) throw updateError;

      setPhotos(updatedPhotos);
      onPhotosUpdate(updatedPhotos);

    } catch (error) {
      console.error('Photo removal error:', error);
      alert('Failed to remove photo');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-slate-300">Equipment Photos</h4>
        <span className="text-xs text-slate-500">{photos.length}/{maxPhotos}</span>
      </div>

      {/* Photo Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {photos.map((photoUrl, index) => (
          <div key={index} className="relative group">
            <img
              src={photoUrl}
              alt={`Equipment photo ${index + 1}`}
              className="w-full h-24 object-cover rounded-lg border border-slate-700"
            />
            <button
              onClick={() => removePhoto(photoUrl)}
              className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
            >
              ×
            </button>
          </div>
        ))}

        {/* Upload Button */}
        {photos.length < maxPhotos && (
          <div
            className="w-full h-24 border-2 border-dashed border-slate-600 rounded-lg flex items-center justify-center cursor-pointer hover:border-slate-500 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <div className="flex flex-col items-center">
                <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                <span className="text-xs text-slate-400 mt-1">Uploading...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="text-xs text-slate-400 mt-1">Add Photo</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Upload Guidelines */}
      <div className="text-xs text-slate-500 space-y-1">
        <p>• Maximum {maxPhotos} photos per equipment</p>
        <p>• Supported formats: JPG, PNG, WebP</p>
        <p>• Maximum file size: 5MB per photo</p>
        <p>• Recommended: Installation photos, model plates, condition photos</p>
      </div>
    </div>
  );
};

export default PhotoUpload;